import { useEffect, useRef, useState } from 'react';
import { ApolloClient, NormalizedCacheObject, useApolloClient } from '@apollo/client';
import Bugsnag from '@bugsnag/js';
import type { FormEvent } from 'react';
import { Box, Input, Spinner, Tab, Tabs, Text } from '@99designs/emu';
import { IconSearch1 } from '@99designs/icons';
import { trackEvent } from '@99designs/tracking';
import { DesignGrid } from '../components/DesignGrid';
import { DesignPagination } from '../components/DesignPagination';
import { NoResultsBrowse, NoResultsShortlist } from '../components/NoResults';
import { getDesignInspirationByCategory } from '../util/getDesignInspirationByCategory';
import type { Design, SelectionState, UpdateType } from '../types';

type DisplayableContent =
    | 'UNKNOWN'
    | 'LOADING'
    | 'EMPTY_SHORTLIST'
    | 'EMPTY_BROWSE'
    | 'SHORTLIST_CONTENT'
    | 'BROWSE_CONTENT';

const DESIGN_UPDATED_EVENT = 'InspirationPickerDesignUpdated';

export interface InspirationTabbedProps {
    selectedDesigns: Design[];
}

export function InspirationTabbed({ selectedDesigns }: InspirationTabbedProps) {
    const [activeTab, setActiveTab] = useState<SelectionState>('BROWSE');
    const [designs, setDesigns] = useState<Design[]>([]);
    const apolloClient = useApolloClient() as ApolloClient<NormalizedCacheObject>;
    const [shortListedDesigns, setShortListedDesigns] = useState<Design[]>([]);
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(false);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const searchInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        async function fetchDesigns() {
            setLoading(true);
            const { designs, totalPages } = await getDesignInspirationByCategory(
                apolloClient,
                'logo-design',
                [searchText],
                currentPage,
                12
            );
            setDesigns(designs);
            setTotalPages(totalPages);
            setLoading(false);
        }
        fetchDesigns();
        trackEvent('Performed Search', {
            location: 'brief-inspiration-picker',
            searchTerm: searchText,
            category: 'logo-design',
        });
    }, [searchText, apolloClient, currentPage]);

    useEffect(() => {
        if (selectedDesigns && selectedDesigns.length > 0) {
            setShortListedDesigns(selectedDesigns);
        }
    }, [selectedDesigns]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchText]);

    const onDesignUpdated = (design: Design, updateType: UpdateType) => {
        switch (updateType) {
            case 'ADD':
                setShortListedDesigns([...shortListedDesigns, design]);
                break;
            case 'REMOVE':
                setShortListedDesigns(
                    shortListedDesigns.filter((singleDesign) => singleDesign.id !== design.id)
                );
                break;
        }

        window.dispatchEvent(
            new window.CustomEvent(DESIGN_UPDATED_EVENT, {
                detail: {
                    updateType: updateType,
                    designId: design.id,
                    designer: design.designer,
                    imageUrl: design.image,
                },
            })
        );
    };

    function getContentTypeToBeDisplayed(): DisplayableContent {
        if (loading) {
            return 'LOADING';
        }
        if (activeTab === 'BROWSE') {
            if (designs.length > 0) {
                return 'BROWSE_CONTENT';
            }
            trackEvent('Viewed Empty State', {
                empty_state_type: 'browse',
            });
            return 'EMPTY_BROWSE';
        }
        if (activeTab === 'SHORTLIST') {
            if (shortListedDesigns.length > 0) {
                return 'SHORTLIST_CONTENT';
            }
            trackEvent('Viewed Empty State', {
                empty_state_type: 'shortlist',
            });
            return 'EMPTY_SHORTLIST';
        }
        return 'UNKNOWN';
    }
    return (
        <div>
            <Box
                alignItems={['stretch', 'flex-start']}
                display="flex"
                flexDirection={['column', 'row']}
                justifyContent={['center', 'space-between']}
                marginBottom={['1bu', '1.5bu']}
            >
                <Box flexGrow={1} display="flex" justifyContent={['center', 'flex-start']}>
                    <Tabs aria-label={__placeholder('Inspiration Tabs')}>
                        {['BROWSE', 'SHORTLIST'].map((id) => (
                            <Tab
                                key={id}
                                badge={id === 'SHORTLIST' ? shortListedDesigns.length : undefined}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setActiveTab(id as SelectionState);
                                }}
                                href="#"
                                active={activeTab === id}
                            >
                                {id === 'SHORTLIST'
                                    ? __placeholder('Shortlist')
                                    : __placeholder('Browse')}
                            </Tab>
                        ))}
                    </Tabs>
                </Box>
                <Box flexGrow={2} paddingTop={'none'}>
                    {activeTab === 'BROWSE' && (
                        <form
                            onSubmit={(event: FormEvent<HTMLFormElement>) => {
                                event.preventDefault();
                                if (searchInputRef.current) {
                                    setSearchText(searchInputRef.current.value);
                                }
                            }}
                        >
                            <Input
                                icon={IconSearch1}
                                id="inspiration-search-input"
                                aria-label="inspiration-search"
                                ref={searchInputRef}
                                role="search"
                                defaultValue={searchText}
                                placeholder={__placeholder(
                                    'Search keywords e.g. retro, minimal, bear'
                                )}
                            />
                        </form>
                    )}
                </Box>
            </Box>

            {(() => {
                switch (getContentTypeToBeDisplayed()) {
                    case 'LOADING':
                        return (
                            <Box
                                display="flex"
                                alignItems="center"
                                flexDirection="column"
                                paddingTop="4bu"
                            >
                                <Spinner size="large" />
                                <Text>{__placeholder('Loading...')}</Text>
                            </Box>
                        );
                    case 'EMPTY_SHORTLIST':
                        return (
                            <Box display="flex" justifyContent="center">
                                <NoResultsShortlist onCtaClick={() => setActiveTab('BROWSE')} />
                            </Box>
                        );
                    case 'EMPTY_BROWSE':
                        return (
                            <Box display="flex" justifyContent="center">
                                <NoResultsBrowse
                                    onCtaClick={() => {
                                        if (searchInputRef.current) {
                                            searchInputRef.current.value = '';
                                        }
                                        setSearchText('');
                                    }}
                                />
                            </Box>
                        );
                    case 'SHORTLIST_CONTENT':
                        return (
                            <Box display="flex" flexWrap="wrap" justifyContent="space-between">
                                <DesignGrid
                                    designs={shortListedDesigns}
                                    likedIds={[]}
                                    variant="SHORTLIST"
                                    onDesignUpdated={onDesignUpdated}
                                />
                            </Box>
                        );
                    case 'BROWSE_CONTENT':
                        return (
                            <>
                                <Box display="flex" flexWrap="wrap" justifyContent="space-between">
                                    <DesignGrid
                                        designs={designs}
                                        likedIds={shortListedDesigns.map(
                                            (design) => design.id as number
                                        )}
                                        variant="BROWSE"
                                        onDesignUpdated={onDesignUpdated}
                                    />
                                </Box>
                                <DesignPagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={(targetPage) => setCurrentPage(targetPage)}
                                />
                            </>
                        );
                    default: {
                        Bugsnag.notify(
                            new Error('Unhandled state for InspirationTabbed'),
                            (event) => {
                                event.context = `loading=[${loading}]; activeTab=[${activeTab}]; designsLength=[${designs.length}]; shortListedDesignsLength=[${shortListedDesigns.length}];`;
                            }
                        );
                        return (
                            <div>
                                {__placeholder(
                                    'An unexpected error occurred. Please contact support or try again.'
                                )}
                            </div>
                        );
                    }
                }
            })()}
        </div>
    );
}
